import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api } from "@/api";
import { PageHeader, EmptyState } from "@/components/Shared";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Trash2, Wrench, Receipt } from "lucide-react";

const ENTITIES = ["vendors", "clients", "products", "invoices", "quotations", "income", "expense"];
const selCls = "h-10 rounded-lg border border-slate-300 bg-slate-50/80 placeholder:text-slate-400 focus:bg-white focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500/20 hover:border-slate-400 transition-all duration-150 outline-none cursor-pointer px-3 text-sm text-slate-900";

export default function CustomFields() {
  const [fields, setFields] = useState([]);
  const [entity, setEntity] = useState("vendors");
  const [label, setLabel] = useState("");
  const [fieldType, setFieldType] = useState("text");
  const [showOnInvoice, setShowOnInvoice] = useState(false);
  const [busy, setBusy] = useState(false);

  const load = () => api.get("/custom-fields").then((r) => setFields(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const add = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/custom-fields", { entity, label, field_type: fieldType, show_on_invoice: showOnInvoice });
      toast.success(`Field "${label}" added to ${entity} form — live immediately`);
      setLabel(""); setShowOnInvoice(false);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to add field");
    } finally { setBusy(false); }
  };

  const toggleInvoice = async (f) => {
    try {
      await api.put(`/custom-fields/${f.field_id}`, { show_on_invoice: !f.show_on_invoice });
      toast.success(f.show_on_invoice ? `"${f.label}" hidden from invoices` : `"${f.label}" will now print on invoices`);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Toggle failed");
    }
  };

  const remove = async (f) => {
    if (!window.confirm(`Remove field "${f.label}" from ${f.entity}?`)) return;
    try {
      await api.delete(`/custom-fields/${f.field_id}`);
      toast.success("Field removed");
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Remove failed");
    }
  };

  const invoiceEntities = ["invoices", "clients", "quotations"];

  return (
    <div data-testid="portal-custom-fields-admin">
      <PageHeader title="Custom Fields Engine" sub="Admin-defined extra fields appear instantly on the matching form — flag the ones that should also print on invoices" />

      <form onSubmit={add} className="rounded-2xl border border-emerald-500/25 bg-white p-5 mb-8 flex flex-wrap items-end gap-3" data-testid="custom-field-form">
        <div className="space-y-1.5">
          <Label className="text-xs font-semibold text-slate-800 uppercase tracking-wider">Form / Tab</Label>
          <select value={entity} onChange={(e) => setEntity(e.target.value)} data-testid="custom-field-entity-select" className={`${selCls} capitalize`}>
            {ENTITIES.map((en) => <option key={en} value={en} className="capitalize">{en}</option>)}
          </select>
        </div>
        <div className="space-y-1.5 flex-1 min-w-[180px]">
          <Label className="text-xs font-semibold text-slate-800 uppercase tracking-wider">Field Label</Label>
          <Input required value={label} onChange={(e) => setLabel(e.target.value)} placeholder="e.g. Project Code, Subsidy Tag" data-testid="custom-field-label-input" className="bg-slate-50/80 border-slate-300 placeholder:text-slate-400 focus:bg-white focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500/20 hover:border-slate-400 transition-all duration-150 outline-none text-slate-900" />
        </div>
        <div className="space-y-1.5">
          <Label className="text-xs font-semibold text-slate-800 uppercase tracking-wider">Type</Label>
          <select value={fieldType} onChange={(e) => setFieldType(e.target.value)} data-testid="custom-field-type-select" className={selCls}>
            <option value="text">Text</option>
            <option value="number">Number</option>
            <option value="date">Date</option>
          </select>
        </div>
        {invoiceEntities.includes(entity) && (
          <label className="flex items-center gap-2 text-sm text-slate-700 pb-2.5" data-testid="custom-field-invoice-flag">
            <input type="checkbox" checked={showOnInvoice} onChange={(e) => setShowOnInvoice(e.target.checked)} data-testid="custom-field-show-on-invoice" className="accent-emerald-500 w-4 h-4" />
            Show on invoice
          </label>
        )}
        <Button type="submit" disabled={busy} data-testid="custom-field-add-btn"
          className="bg-emerald-500 hover:bg-emerald-400 text-slate-950 font-semibold rounded-full transition-colors duration-200">
          {busy ? "Adding…" : "Add Field"}
        </Button>
      </form>

      {fields.length === 0 ? (
        <EmptyState icon={Wrench} title="No custom fields yet" sub="Add one above — it will appear on that form right away" />
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {ENTITIES.map((en) => {
            const list = fields.filter((f) => f.entity === en);
            if (!list.length) return null;
            return (
              <div key={en} className="rounded-2xl border border-slate-200 bg-white p-5" data-testid={`custom-fields-group-${en}`}>
                <p className="text-xs font-mono2 uppercase tracking-[0.18em] text-emerald-600 mb-4 capitalize">{en} form</p>
                <div className="space-y-2">
                  {list.map((f) => (
                    <div key={f.field_id} className="flex items-center justify-between rounded-xl bg-slate-100 px-4 py-2.5" data-testid={`custom-field-row-${f.field_id}`}>
                      <div>
                        <span className="text-sm text-slate-900">{f.label}</span>
                        <span className="ml-2 text-[10px] font-mono2 uppercase tracking-wider text-slate-500">{f.field_type}</span>
                      </div>
                      <div className="flex items-center gap-1.5">
                        {invoiceEntities.includes(f.entity) && (
                          <button onClick={() => toggleInvoice(f)} data-testid={`custom-field-invoice-toggle-${f.field_id}`}
                            className={`inline-flex items-center gap-1 text-[11px] px-2.5 py-1 rounded-full border transition-colors duration-150 ${
                              f.show_on_invoice
                                ? "border-emerald-500/50 text-emerald-600 bg-emerald-500/10"
                                : "border-slate-600 text-slate-500 hover:text-slate-700"
                            }`}>
                            <Receipt className="w-3 h-3" /> {f.show_on_invoice ? "On invoice" : "Not on invoice"}
                          </button>
                        )}
                        <button onClick={() => remove(f)} data-testid={`custom-field-delete-${f.field_id}`}
                          className="text-red-600 hover:text-red-700 p-1 rounded-lg hover:bg-red-500/10 transition-colors duration-150">
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
